import { useEffect, useMemo, useState } from 'react'
import { Apple, ArrowRight, Download, Loader2, Monitor } from 'lucide-react'
import api from '../../utils/api'

function DownloadButtons({ theme = 'cyan', source }) {
  const [downloads, setDownloads] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(null)

  useEffect(() => {
    let mounted = true

    const fetchDownloads = async () => {
      try {
        const response = await api.get('/downloads')
        if (mounted && response.data.success) {
          setDownloads(response.data.downloads || [])
        }
      } catch (error) {
        console.error('获取下载列表失败:', error)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    fetchDownloads()
    return () => {
      mounted = false
    }
  }, [])

  const palette = useMemo(() => {
    const map = {
      cyan: {
        card: 'border-cyan-100 bg-white hover:border-cyan-300 hover:shadow-[0_24px_60px_-30px_rgba(34,211,238,0.45)]',
        icon: 'bg-cyan-50 text-cyan-600',
        button: 'border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
      },
      emerald: {
        card: 'border-emerald-100 bg-white hover:border-emerald-300 hover:shadow-[0_24px_60px_-30px_rgba(16,185,129,0.35)]',
        icon: 'bg-emerald-50 text-emerald-600',
        button: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
      },
      violet: {
        card: 'border-violet-100 bg-white hover:border-violet-300 hover:shadow-[0_24px_60px_-30px_rgba(139,92,246,0.38)]',
        icon: 'bg-violet-50 text-violet-600',
        button: 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'
      },
      amber: {
        card: 'border-amber-100 bg-white hover:border-amber-300 hover:shadow-[0_24px_60px_-30px_rgba(245,158,11,0.35)]',
        icon: 'bg-amber-50 text-amber-600',
        button: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
      },
      rose: {
        card: 'border-rose-100 bg-white hover:border-rose-300 hover:shadow-[0_24px_60px_-30px_rgba(244,63,94,0.28)]',
        icon: 'bg-rose-50 text-rose-600',
        button: 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
      }
    }
    return map[theme] || map.cyan
  }, [theme])

  const fallbackItems = [
    { id: 'windows', label: 'Windows 版本', helper: '适合 Windows 10/11 用户', icon: Monitor },
    { id: 'macos-intel', label: 'Mac Intel 版本', helper: '适合 Intel 芯片 Mac', icon: Apple },
    { id: 'macos-apple', label: 'Mac Apple 版本', helper: '适合 Apple Silicon 芯片 Mac', icon: Apple }
  ]

  const actualItems = fallbackItems.map((item) => {
    const match = downloads.find((download) => download.id === item.id)
    return {
      ...item,
      id: match?.id || item.id,
      filename: match?.filename,
      file_path: match?.file_path,
      version: match?.version,
      label: match?.name_zh ? `下载 ${match.name_zh}` : `下载 ${item.label}`
    }
  })

  const handleDownload = async (id) => {
    const targetDownload = downloads.find((download) => download.id === id)
    if (!targetDownload || !targetDownload.file_path) {
      window.alert('安装包即将上线，敬请期待！')
      return
    }

    setDownloading(id)
    const sourceParam = source ? `&source=${encodeURIComponent(String(source))}` : ''

    try {
      const response = await api.get(`/downloads/${id}?t=${Date.now()}${sourceParam}`, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      })

      const contentType = response.headers?.['content-type'] || response.data?.type || ''
      if (contentType.includes('application/json')) {
        const reader = new FileReader()
        reader.onload = () => {
          const parsed = JSON.parse(reader.result)
          const data = parsed.data || parsed
          if (data.redirectUrl) {
            const finalUrl = data.redirectUrl.startsWith('/')
              ? `${window.location.origin}${data.redirectUrl}`
              : data.redirectUrl
            window.location.href = finalUrl
          } else {
            window.alert(data.message || parsed.message || '安装包即将上线，敬请期待！')
          }
        }
        reader.readAsText(response.data)
      } else {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', targetDownload?.filename || `OpenClaw-${id}`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      if (error.response?.data?.message) {
        window.alert(error.response.data.message)
      } else {
        window.alert('下载失败，请稍后重试')
      }
    } finally {
      setDownloading(null)
    }
  }

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {loading ? (
        <div className="col-span-full flex items-center justify-center rounded-3xl border border-slate-200 bg-white px-6 py-12 text-slate-600 shadow-sm">
          <Loader2 className="mr-3 h-6 w-6 animate-spin" />
          正在获取下载信息...
        </div>
      ) : (
        actualItems.map((item) => {
          const Icon = item.icon
          const isBusy = downloading === item.id
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleDownload(item.id)}
              disabled={isBusy}
              className={`group rounded-3xl border p-6 text-left text-slate-900 transition duration-300 ${palette.card}`}
            >
              <div className={`mb-5 inline-flex rounded-2xl p-3 ${palette.icon}`}>
                <Icon className="h-6 w-6" />
              </div>
              <div className="mb-2 text-lg font-semibold">{item.label}</div>
              <div className="mb-5 text-sm leading-6 text-slate-500">
                {item.helper}
                {item.version ? ` · 当前版本 ${item.version}` : ''}
              </div>
              <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${palette.button}`}>
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                立即下载
                {!isBusy && <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />}
              </div>
            </button>
          )
        })
      )}
    </div>
  )
}

export default DownloadButtons
